import React, { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { channelApi } from '../utils/api';
import { useWebSocket } from '../hooks/useWebSocket';
import Sidebar from '../components/Sidebar';
import RewardSettings from '../components/RewardSettings';
import QueueManagement from '../components/QueueManagement';
import WidgetInfo from '../components/WidgetInfo';
import { Channel } from '../types';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [channelId, setChannelId] = useState<number | null>(null);

  const { isConnected, lastMessage } = useWebSocket(channelId);

  const getCurrentTab = () => {
    const path = location.pathname.split('/dashboard/')[1];
    return path || 'overview';
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const channelIdFromUrl = urlParams.get('channel_id');
    const channelIdFromStorage = localStorage.getItem('channelId');
    const id = channelIdFromUrl || channelIdFromStorage;

    if (id) {
      setChannelId(parseInt(id));
      localStorage.setItem('channelId', id);
      if (channelIdFromUrl) {
        window.history.replaceState({}, '', location.pathname);
      }
    } else {
      navigate('/login');
    }
  }, [navigate, location.pathname]);

  const { data: channel, isLoading, error } = useQuery<Channel>({
    queryKey: ['channel', channelId],
    queryFn: () => channelApi.getChannel(channelId!).then(res => res.data),
    enabled: !!channelId,
  });

  const handleLogout = () => {
    localStorage.removeItem('channelId');
    localStorage.removeItem('authToken');
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 24, height: 24, border: '2px solid #e5e5e5', borderTopColor: '#6441a5', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  if (error || !channel) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#555', marginBottom: 16 }}>Failed to load channel</p>
          <button onClick={() => navigate('/login')} style={{ fontSize: 13, color: '#6441a5', background: 'none', border: 'none', cursor: 'pointer' }}>
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f9f9f9' }}>
      <Sidebar
        currentTab={getCurrentTab()}
        isConnected={isConnected}
        displayName={channel.display_name}
        onLogout={handleLogout}
      />

      <main style={{ flex: 1, padding: 32, maxWidth: 720 }}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard/overview" replace />} />
          <Route path="/overview" element={<WidgetInfo channelId={channelId!} />} />
          <Route path="/youtube/*" element={
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard/youtube/settings" replace />} />
              <Route path="/settings" element={<RewardSettings channelId={channelId!} />} />
              <Route path="/queue" element={<QueueManagement channelId={channelId!} lastMessage={lastMessage} />} />
            </Routes>
          } />
        </Routes>
      </main>
    </div>
  );
};

export default DashboardPage;
